import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React from 'react';
import { withSafeTimeout } from '@hocs/safe-timers';
import { compose, defaultProps, pure, withHandlers, withProps, withPropsOnChange, withState } from 'recompose';
import { USDFormatterRound } from '../../utils/number';
import { CURRENCY_INPUT_CENTS, CURRENCY_INPUT_DOLLARS } from '../../utils/testing/TestIDs';
import { ScreenReaderOnly } from '../../ScreenReaderOnly';
import { CentInput, Container, DollarInput, Error, HelperText, HundredthPlaceholder, InputContainer, InputPrefix, Label } from './styled-components';
import { getKey, sanitize, scheduleSelectionChange } from './utils';
import { withErrorProps } from './with-error-props';
import { withInputWidth } from './with-input-width';
import { withRef } from './with-ref';
var ARROW_LEFT = 'arrowleft';
var ARROW_RIGHT = 'arrowright';
var BACKSPACE = 'backspace';
var PERIOD = '.'; // Note: the value prop must be passed as a string due to how we are handling the editing
// of the cents field. Passing the value as a Float adds too much complexity and introduces
// additional edge cases.

var enhance = compose(withSafeTimeout, defaultProps({
  maxDollarLength: 5
}), withState('centValue', 'setCentValue', ''), withState('dollarValue', 'setDollarValue', ''), withState('editingCents', 'setEditingCents', false), withState('shake', 'setShake', false), withRef('dollarInput', 'setDollarInput'), withRef('centInput', 'setCentInput'), // Consumes provided value prop and splits into separate values for inputs
withPropsOnChange(['value'], function (_ref) {
  var centValue = _ref.centValue,
      dollarValue = _ref.dollarValue,
      setCentValue = _ref.setCentValue,
      setDollarValue = _ref.setDollarValue,
      setEditingCents = _ref.setEditingCents,
      value = _ref.value;

  var _value$split = value.split(PERIOD),
      _value$split2 = _slicedToArray(_value$split, 2),
      parsedDollar = _value$split2[0],
      _value$split2$ = _value$split2[1],
      parsedCent = _value$split2$ === void 0 ? '' : _value$split2$;

  if (parsedDollar !== dollarValue) {
    setDollarValue(parsedDollar);
  }

  if (parsedCent !== centValue) {
    setCentValue(parsedCent);
  }

  if (parsedCent) {
    setEditingCents(true);
  }

  return {
    hasCentValue: !!parsedCent.length
  };
}), // Adds comma delimiters to dollar input value
withPropsOnChange(['dollarValue'], function (_ref2) {
  var dollarValue = _ref2.dollarValue;
  return {
    formattedDollarValue: dollarValue ? USDFormatterRound.format(parseFloat(dollarValue)).replace('$', '') : ''
  };
}), // Forces editingCents to true if cent input has value
withProps(function (_ref3) {
  var centValue = _ref3.centValue,
      editingCents = _ref3.editingCents;
  return {
    editingCents: centValue ? true : editingCents
  };
}), // Dynamically calculates dollar input width
withInputWidth('formattedDollarValue'), // Sets error props and colors
withErrorProps, withHandlers({
  // Handler for informing parent component of value change
  informChanged: function informChanged(_ref4) {
    var onChange = _ref4.onChange,
        value = _ref4.value,
        setDollarValue = _ref4.setDollarValue,
        maxDollarLength = _ref4.maxDollarLength,
        setShake = _ref4.setShake,
        setSafeTimeout = _ref4.setSafeTimeout;
    return function (dollarValue, centValue) {
      var newValue = dollarValue;

      if (centValue) {
        newValue = "".concat(dollarValue, ".").concat(centValue);
      }

      if (newValue !== value) {
        onChange(newValue);
      }

      if (!centValue.length && dollarValue === '0') {
        setDollarValue('');
      }

      if (dollarValue.length === maxDollarLength || !newValue.length) {
        setShake(true);
        setSafeTimeout(function () {
          setShake(false);
        }, 600);
      }
    };
  },
  // Moves focus between the two inputs and toggles visibility of cents input
  moveFocus: function moveFocus(_ref5) {
    var setEditingCents = _ref5.setEditingCents,
        centInput = _ref5.centInput,
        dollarInput = _ref5.dollarInput;
    return function (event, editingCents) {
      setEditingCents(true);
      var input = editingCents ? centInput : dollarInput;
      input.focus(); // Workaround for Safari's stupidity

      setTimeout(function () {
        return input.focus();
      }, 0);
      event.preventDefault();
    };
  }
}), withHandlers({
  // On change handlers
  handleDollarChange: function handleDollarChange(_ref6) {
    var centValue = _ref6.centValue,
        informChanged = _ref6.informChanged,
        maxDollarLength = _ref6.maxDollarLength,
        moveFocus = _ref6.moveFocus,
        setDollarValue = _ref6.setDollarValue;
    return function (_ref7) {
      var target = _ref7.target,
          event = _objectWithoutProperties(_ref7, ["target"]);

      var value = target.value; // Workaround for Android devices

      if (value.indexOf(PERIOD) > -1) {
        moveFocus(event, true);
        return;
      }

      value = sanitize(value);

      if (value.length > maxDollarLength) {
        value = value.substring(0, maxDollarLength);
      }

      scheduleSelectionChange(target);
      setDollarValue(value);
      informChanged(value, centValue);
    };
  },
  handleCentChange: function handleCentChange(_ref8) {
    var setCentValue = _ref8.setCentValue,
        informChanged = _ref8.informChanged,
        dollarValue = _ref8.dollarValue,
        setShake = _ref8.setShake,
        setSafeTimeout = _ref8.setSafeTimeout;
    return function (_ref9) {
      var target = _ref9.target;
      var value = target.value;
      value = sanitize(value);

      if (value.length > 2) {
        setShake(true);
        setSafeTimeout(function () {
          setShake(false);
        }, 600);
        return;
      }

      scheduleSelectionChange(target);
      setCentValue(value);
      informChanged(dollarValue, value);
    };
  },
  // On key down handlers
  // Note: key codes do not work on some Android devices
  handleDollarKeyDown: function handleDollarKeyDown(_ref10) {
    var formattedDollarValue = _ref10.formattedDollarValue,
        moveFocus = _ref10.moveFocus,
        setShake = _ref10.setShake,
        dollarValue = _ref10.dollarValue,
        setSafeTimeout = _ref10.setSafeTimeout;
    return function (event) {
      var key = getKey(event);

      if (key === PERIOD || key === ARROW_RIGHT && (!formattedDollarValue || event.target.selectionStart === formattedDollarValue.length)) {
        moveFocus(event, true);
      }

      if (key === BACKSPACE && !dollarValue) {
        setShake(true);
        setSafeTimeout(function () {
          setShake(false);
        }, 600);
      }
    };
  },
  handleCentKeyDown: function handleCentKeyDown(_ref11) {
    var moveFocus = _ref11.moveFocus;
    return function (event) {
      var key = getKey(event);

      if (event.target.selectionStart === 0 && (key === ARROW_LEFT || key === BACKSPACE)) {
        moveFocus(event, false);
      }
    };
  },
  // On blur handlers
  handleCentBlur: function handleCentBlur(_ref12) {
    var centValue = _ref12.centValue,
        setCentValue = _ref12.setCentValue,
        setEditingCents = _ref12.setEditingCents;
    return function () {
      if (centValue.length === 1) {
        setCentValue("".concat(centValue, "0"));
      } else if (centValue.length === 0) {
        setEditingCents(false);
      }
    };
  }
}), pure);
var CurrencyInput = enhance(function (_ref13) {
  var _ref13$hasHiddenLabel = _ref13.hasHiddenLabel,
      hasHiddenLabel = _ref13$hasHiddenLabel === void 0 ? true : _ref13$hasHiddenLabel,
      props = _objectWithoutProperties(_ref13, ["hasHiddenLabel"]);

  var dollarInput = 'dollars';
  var centInput = 'cents';
  var label = typeof props.label === 'string' ? /*#__PURE__*/React.createElement(Label, null, props.label) : props.label;
  return /*#__PURE__*/React.createElement(Container, {
    shake: props.shake,
    className: props.className
  }, /*#__PURE__*/React.createElement(InputContainer, {
    role: "group",
    "aria-label": hasHiddenLabel ? props.name : null,
    "aria-labelledby": hasHiddenLabel ? null : props.name
  }, hasHiddenLabel ? /*#__PURE__*/React.createElement(ScreenReaderOnly, null, /*#__PURE__*/React.createElement("span", null, label)) : label, /*#__PURE__*/React.createElement(InputPrefix, {
    "data-error-color": props.valueErrorColor,
    primaryColorOff: props.dollarValue.length === 0
  }, "$"), /*#__PURE__*/React.createElement(ScreenReaderOnly, null, /*#__PURE__*/React.createElement("label", {
    htmlFor: dollarInput
  }, "Dollars")), /*#__PURE__*/React.createElement(DollarInput, {
    autoFocus: props.autoFocus && !props.hasCentValue,
    "data-error-color": props.valueErrorColor,
    "data-testid": CURRENCY_INPUT_DOLLARS,
    id: "dollar",
    name: dollarInput,
    onChange: props.handleDollarChange,
    onKeyDown: props.handleDollarKeyDown,
    placeholder: "0",
    readOnly: props.readOnly,
    ref: props.setDollarInput,
    style: {
      width: props.width
    },
    value: props.formattedDollarValue,
    inputMode: "numeric"
  }), /*#__PURE__*/React.createElement(ScreenReaderOnly, null, /*#__PURE__*/React.createElement("label", {
    htmlFor: centInput
  }, "Cents")), /*#__PURE__*/React.createElement(CentInput, {
    autoFocus: props.autoFocus && props.hasCentValue,
    "data-error-color": props.valueErrorColor,
    "data-testid": CURRENCY_INPUT_CENTS,
    id: "cents",
    name: centInput,
    onBlur: props.handleCentBlur,
    onChange: props.handleCentChange,
    onKeyDown: props.handleCentKeyDown,
    placeholder: "00",
    readOnly: props.readOnly,
    ref: props.setCentInput,
    style: {
      opacity: props.editingCents ? 1 : 0,
      width: props.editingCents ? props.centWidth : '0'
    },
    value: props.centValue,
    inputMode: "numeric"
  }), props.centValue.length === 1 && /*#__PURE__*/React.createElement(HundredthPlaceholder, null, "0"), props.helperText && /*#__PURE__*/React.createElement(HelperText, {
    readOnly: props.readOnly
  }, props.helperText)), props.valueError && /*#__PURE__*/React.createElement(Error, {
    "data-testid": "error",
    color: props.valueErrorColor
  }, props.valueError));
});
export { CurrencyInput };